import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Users } from "lucide-react";

export default function SchoolDelegatesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [level, setLevel] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", grade: "", experience_level: "beginner",
  });

  const canManage = user?.role === "school_admin";
  const LIMIT = 12;

  const load = async () => {
    const q = new URLSearchParams({
      page: String(page),
      limit: String(LIMIT),
      sort,
    });
    if (search) q.set("search", search);
    if (level) q.set("experience_level", level);
    if (user?.school_id) q.set("school_id", user.school_id);
    const { data } = await api.get(`/v2/delegates?${q.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, search, sort, level, user?.school_id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/v2/delegates", {
        ...form,
        school_id: user.school_id,
      });
      setOpen(false);
      setForm({ name: "", email: "", phone: "", grade: "", experience_level: "beginner" });
      load();
      toast.success("Delegate added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Roster
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              {canManage ? "Our delegates" : "Students in my care"}
            </h1>
            <p className="mt-2 text-slate-500 text-sm">Total: {total}</p>
          </div>
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="add-delegate-button">
                  <Plus className="h-4 w-4 mr-1" /> Add delegate
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add a delegate</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-4" data-testid="delegate-form">
                  <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Grade</Label><Input value={form.grade} onChange={(e) => setForm({...form, grade: e.target.value})} placeholder="11" /></div>
                    <div>
                      <Label>Experience</Label>
                      <Select value={form.experience_level} onValueChange={(v) => setForm({...form, experience_level: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="delegate-submit-button">Save</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search name or email…"
              className="pl-10 h-11"
              data-testid="search-input"
            />
          </div>
          <Select value={level} onValueChange={(v) => { setPage(1); setLevel(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-[180px] h-11" data-testid="filter-level"><SelectValue placeholder="Any level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => { setPage(1); setSort(v); }}>
            <SelectTrigger className="w-[180px] h-11" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="-created_at">Newest</SelectItem>
              <SelectItem value="created_at">Oldest</SelectItem>
              <SelectItem value="name">Name A→Z</SelectItem>
              <SelectItem value="-name">Name Z→A</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="mt-6 rounded-xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Delegate</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Grade</th>
                <th className="text-left px-5 py-3">Experience</th>
                <th className="text-left px-5 py-3">Dietary</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-${d.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0A192F]">{d.name}</td>
                  <td className="px-5 py-3 text-slate-600">{d.email}</td>
                  <td className="px-5 py-3 font-mono text-xs">{d.grade || "—"}</td>
                  <td className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-bold">
                    <span className={`px-2 py-1 rounded-full ${
                      d.experience_level === "advanced" ? "bg-[#0A192F] text-[#D4AF37]"
                      : d.experience_level === "intermediate" ? "bg-[#D4AF37]/15 text-[#0A192F]"
                      : "bg-slate-100 text-slate-600"
                    }`}>
                      {d.experience_level || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">{d.dietary || "—"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12">
                  <Users className="h-5 w-5 text-slate-400 mx-auto" />
                  <p className="mt-2 text-sm text-slate-500">No delegates match.</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-slate-500">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page-button">Prev</Button>
            <Button variant="outline" disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))} data-testid="next-page-button">Next</Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
